import { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, subDays } from 'date-fns';
import {
  fetchAnalyticsStats, fetchPageViews, fetchReferrers, fetchDevices, fetchBrowsers, fetchDailyStats,
  fetchActiveSessions, fetchRecentEvents, fetchGeographicalData, fetchOSData,
  fetchUTMCampaigns, fetchVisitors, fetchVisitorDetails,
  AnalyticsStats, PageViewData, ReferrerData, DeviceData, BrowserData, DailyStatsData,
  ActiveSessionsData, RecentEvent, GeographicalData, OSData, UTMCampaignData,
  VisitorListResponse
} from '../api/analytics';
import { VisitorDetailsModal } from '../components/VisitorDetailsModal';

const COLORS = {
  primary: '#10b981', secondary: '#3b82f6', accent: '#8b5cf6', warning: '#f59e0b',
  danger: '#ef4444', success: '#22c55e', info: '#06b6d4', purple: '#a855f7', pink: '#ec4899',
  chartColors: ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#22c55e']
};

interface StatCardProps { title: string; value: number | string; icon: string; trend?: string; color?: string; subtitle?: string; }
const StatCard = ({ title, value, icon, trend, color = COLORS.primary, subtitle }: StatCardProps) => (
  <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', padding: '24px', borderRadius: '16px', border: '1px solid #334155', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', transition: 'transform 0.2s', cursor: 'default' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div style={{ flex: 1 }}>
        <div style={{ color: '#94a3b8', fontSize: '13px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>{title}</div>
        <div style={{ color: '#f1f5f9', fontSize: '32px', fontWeight: '700', lineHeight: '1' }}>{value}</div>
        {subtitle && <div style={{ color: '#64748b', fontSize: '12px', marginTop: '6px' }}>{subtitle}</div>}
        {trend && <div style={{ color: COLORS.success, fontSize: '12px', marginTop: '8px', fontWeight: '600' }}> {trend}</div>}
      </div>
      <div style={{ fontSize: '36px', opacity: 0.9 }}>{icon}</div>
    </div>
  </div>
);

interface ChartCardProps { title: string; children: React.ReactNode; subtitle?: string; height?: string }
const ChartCard = ({ title, children, subtitle, height = 'auto' }: ChartCardProps) => (
  <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', padding: '24px', borderRadius: '16px', border: '1px solid #334155', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', height }}>
    <div style={{ marginBottom: '20px' }}>
      <h3 style={{ color: '#f1f5f9', fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>{title}</h3>
      {subtitle && <p style={{ color: '#64748b', fontSize: '13px' }}>{subtitle}</p>}
    </div>
    {children}
  </div>
);

const Dashboard = () => {
  const [dateRange, setDateRange] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<AnalyticsStats | null>(null);
  const [pageViews, setPageViews] = useState<PageViewData[]>([]);
  const [referrers, setReferrers] = useState<ReferrerData[]>([]);
  const [devices, setDevices] = useState<DeviceData[]>([]);
  const [browsers, setBrowsers] = useState<BrowserData[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyStatsData[]>([]);
  const [activeSessions, setActiveSessions] = useState<ActiveSessionsData | null>(null);
  const [recentEvents, setRecentEvents] = useState<RecentEvent[]>([]);
  const [geoData, setGeoData] = useState<GeographicalData[]>([]);
  const [osData, setOSData] = useState<OSData[]>([]);
  const [utmCampaigns, setUTMCampaigns] = useState<UTMCampaignData[]>([]);
  const [visitors, setVisitors] = useState<VisitorListResponse | null>(null);
  const [visitorPage, setVisitorPage] = useState(0);
  const [selectedVisitor, setSelectedVisitor] = useState<any>(null);
  const [showVisitorModal, setShowVisitorModal] = useState(false);
  const [loadingVisitorDetails, setLoadingVisitorDetails] = useState(false);
  const [useLocalTime, setUseLocalTime] = useState(true);

  // Format date based on timezone preference
  const formatTime = (dateStr: string, fmt: string = 'PPp') => {
    const date = new Date(dateStr);
    if (useLocalTime) return format(date, fmt);
    const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return format(utcDate, fmt) + ' UTC';
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      const endDate = new Date();
      const startDate = subDays(endDate, dateRange);
      try {
        const [statsData, pageViewsData, referrersData, devicesData, browsersData, dailyStatsData,
          activeData, eventsData, geoDataRes, osDataRes, utmRes, visitorsRes] = await Promise.all([
            fetchAnalyticsStats(startDate, endDate), fetchPageViews(startDate, endDate), fetchReferrers(startDate, endDate),
            fetchDevices(startDate, endDate), fetchBrowsers(startDate, endDate), fetchDailyStats(startDate, endDate),
            fetchActiveSessions(), fetchRecentEvents(20), fetchGeographicalData(startDate, endDate),
            fetchOSData(startDate, endDate), fetchUTMCampaigns(startDate, endDate),
            fetchVisitors(startDate, endDate, 10, visitorPage * 10)
          ]);
        setStats(statsData); setPageViews(pageViewsData); setReferrers(referrersData);
        setDevices(devicesData); setBrowsers(browsersData); setDailyStats(dailyStatsData);
        setActiveSessions(activeData); setRecentEvents(eventsData); setGeoData(geoDataRes);
        setOSData(osDataRes); setUTMCampaigns(utmRes); setVisitors(visitorsRes);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch analytics data');
      } finally { setLoading(false); }
    };
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [dateRange, visitorPage]);

  const handleVisitorClick = async (visitorId: string) => {
    setLoadingVisitorDetails(true);
    setShowVisitorModal(true);
    try {
      const details = await fetchVisitorDetails(visitorId);
      setSelectedVisitor(details);
    } catch (err) {
      console.error('Failed to fetch visitor details:', err);
      setSelectedVisitor(null);
    } finally {
      setLoadingVisitorDetails(false);
    }
  };

  const handleCloseModal = () => {
    setShowVisitorModal(false);
    setSelectedVisitor(null);
  };

  if (loading) return (<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0f172a' }}><div style={{ textAlign: 'center' }}><div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div><div style={{ color: COLORS.primary, fontSize: '20px', fontWeight: '600' }}>Loading analytics...</div></div></div>);
  if (error) return (<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0f172a' }}><div style={{ textAlign: 'center', maxWidth: '400px' }}><div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div><div style={{ color: COLORS.danger, fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>Error Loading Data</div><div style={{ color: '#94a3b8', fontSize: '14px' }}>{error}</div></div></div>);

  const avgSessionMinutes = Math.round((stats?.avgSessionDuration || 0) / 60);

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', padding: '24px' }}>
      <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h1 style={{ color: '#f1f5f9', fontSize: '36px', fontWeight: '700', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}><span>📊</span> Kokki Analytics</h1>
              <p style={{ color: '#64748b', fontSize: '14px' }}>Comprehensive insights and performance metrics</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#1e293b', padding: '8px', borderRadius: '12px', border: '1px solid #334155' }}>
              <span style={{ color: '#94a3b8', fontSize: '13px', fontWeight: '500' }}>Period:</span>
              {[7, 14, 30, 90].map((days) => (
                <button key={days} onClick={() => setDateRange(days)} style={{ padding: '10px 20px', background: dateRange === days ? COLORS.primary : 'transparent', color: dateRange === days ? '#0f172a' : '#cbd5e1', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: dateRange === days ? '600' : '500', transition: 'all 0.2s' }}>{days}d</button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', padding: '16px 24px', borderRadius: '12px', border: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ fontSize: '24px' }}>👥</div>
              <div>
                <div style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '600' }}>{activeSessions?.activeSessions || 0} Active Users</div>
                <div style={{ color: '#64748b', fontSize: '12px' }}>Currently browsing your site</div>
              </div>
            </div>
            <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', padding: '12px 20px', borderRadius: '12px', border: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ color: '#94a3b8', fontSize: '13px', fontWeight: '500' }}>🕐 Timezone:</span>
              <button onClick={() => setUseLocalTime(true)} style={{ padding: '8px 16px', background: useLocalTime ? COLORS.primary : 'transparent', color: useLocalTime ? '#0f172a' : '#cbd5e1', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: useLocalTime ? '600' : '500' }}>Local</button>
              <button onClick={() => setUseLocalTime(false)} style={{ padding: '8px 16px', background: !useLocalTime ? COLORS.primary : 'transparent', color: !useLocalTime ? '#0f172a' : '#cbd5e1', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: !useLocalTime ? '600' : '500' }}>UTC</button>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '32px' }}>
          <StatCard title="Total Visitors" value={stats?.totalVisitors || 0} icon="👥" color={COLORS.primary} subtitle="Unique visitors" />
          <StatCard title="Page Views" value={stats?.totalPageViews || 0} icon="📄" color={COLORS.secondary} subtitle={`${stats?.avgPagesPerSession || 0} avg per session`} />
          <StatCard title="Sessions" value={stats?.totalSessions || 0} icon="🔄" color={COLORS.accent} subtitle={`${avgSessionMinutes}m avg duration`} />
          <StatCard title="Events Tracked" value={stats?.totalEvents || 0} icon="⚡" color={COLORS.warning} subtitle="User interactions" />
          <StatCard title="New Visitors" value={stats?.newVisitors || 0} icon="🆕" color={COLORS.success} subtitle={`${stats?.totalVisitors ? Math.round((stats.newVisitors / stats.totalVisitors) * 100) : 0}% of total`} />
          <StatCard title="Returning" value={stats?.returningVisitors || 0} icon="🔁" color={COLORS.purple} subtitle="Loyal users" />
        </div>

        <div style={{ marginBottom: '32px' }}>
          <ChartCard title="Traffic Overview" subtitle="Daily sessions and page views">
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={dailyStats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" stroke="#94a3b8" tickFormatter={(date) => format(new Date(date), 'MMM dd')} style={{ fontSize: '12px' }} />
                <YAxis stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#f1f5f9' }} />
                <Legend wrapperStyle={{ fontSize: '13px' }} />
                <Line type="monotone" dataKey="sessions" stroke={COLORS.primary} strokeWidth={3} name="Sessions" dot={{ fill: COLORS.primary, r: 4 }} />
                <Line type="monotone" dataKey="pageViews" stroke={COLORS.secondary} strokeWidth={3} name="Page Views" dot={{ fill: COLORS.secondary, r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '24px', marginBottom: '32px' }}>
          <ChartCard title="Top Pages" subtitle="Most visited pages">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={pageViews.slice(0, 8)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis type="number" stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <YAxis type="category" dataKey="path" stroke="#94a3b8" width={120} style={{ fontSize: '11px' }} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#f1f5f9' }} />
                <Bar dataKey="views" fill={COLORS.primary} name="Views" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Geographical Distribution" subtitle="Visitors by country">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={geoData.slice(0, 8)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="country" stroke="#94a3b8" style={{ fontSize: '11px' }} angle={-45} textAnchor="end" height={80} />
                <YAxis stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#f1f5f9' }} />
                <Bar dataKey="count" fill={COLORS.info} name="Visitors" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '24px', marginBottom: '32px' }}>
          <ChartCard title="Device Breakdown" subtitle="Visitor devices">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={devices} dataKey="count" nameKey="device" cx="50%" cy="50%" outerRadius={100} label labelLine={{ stroke: '#64748b' }}>
                  {devices.map((_entry, index) => (<Cell key={`cell-${index}`} fill={COLORS.chartColors[index % COLORS.chartColors.length]} />))}
                </Pie>
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#f1f5f9' }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Top Browsers" subtitle="Browser usage">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={browsers.slice(0, 6)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="browser" stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <YAxis stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#f1f5f9' }} />
                <Bar dataKey="count" fill={COLORS.secondary} name="Users" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Operating Systems" subtitle="OS distribution">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={osData} dataKey="count" nameKey="os" cx="50%" cy="50%" outerRadius={100} label labelLine={{ stroke: '#64748b' }}>
                  {osData.map((_entry, index) => (<Cell key={`cell-${index}`} fill={COLORS.chartColors[index % COLORS.chartColors.length]} />))}
                </Pie>
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#f1f5f9' }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '24px', marginBottom: '32px' }}>
          <ChartCard title="Traffic Sources" subtitle="Where visitors come from">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={referrers.slice(0, 6)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="source" stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <YAxis stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#f1f5f9' }} />
                <Bar dataKey="sessions" fill={COLORS.accent} name="Sessions" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {utmCampaigns.length > 0 && (
            <ChartCard title="UTM Campaigns" subtitle="Campaign performance">
              <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                {utmCampaigns.slice(0, 6).map((campaign, idx) => (
                  <div key={idx} style={{ background: '#0f172a', padding: '12px 16px', borderRadius: '8px', border: '1px solid #334155', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>{campaign.campaign}</div>
                      <div style={{ color: '#64748b', fontSize: '12px' }}>{campaign.source} • {campaign.medium}</div>
                    </div>
                    <div style={{ color: COLORS.primary, fontSize: '18px', fontWeight: '700' }}>{campaign.sessions}</div>
                  </div>
                ))}
              </div>
            </ChartCard>
          )}
        </div>

        <ChartCard title="Recent Visitors" subtitle={`Showing ${visitors?.visitors.length || 0} of ${visitors?.total || 0} total visitors`}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '13px', fontWeight: '600' }}>Device</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '13px', fontWeight: '600' }}>Browser</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '13px', fontWeight: '600' }}>Location</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '13px', fontWeight: '600' }}>First Visit</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '13px', fontWeight: '600' }}>Sessions</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '13px', fontWeight: '600' }}>Page Views</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '13px', fontWeight: '600' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {visitors?.visitors.map((visitor, idx) => (
                  <tr
                    key={idx}
                    onClick={() => handleVisitorClick(visitor.visitorId)}
                    style={{
                      borderBottom: '1px solid #334155',
                      transition: 'background 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#0f172a'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '12px', color: '#f1f5f9', fontSize: '13px' }}>{visitor.deviceType || 'Unknown'}</td>
                    <td style={{ padding: '12px', color: '#f1f5f9', fontSize: '13px' }}>{visitor.browser || 'Unknown'}</td>
                    <td style={{ padding: '12px', color: '#f1f5f9', fontSize: '13px' }}>{visitor.country && visitor.city ? `${visitor.city}, ${visitor.country}` : visitor.country || 'Unknown'}</td>
                    <td style={{ padding: '12px', color: '#f1f5f9', fontSize: '13px' }}>{format(new Date(visitor.firstVisit), 'MMM dd, yyyy')}</td>
                    <td style={{ padding: '12px', color: '#f1f5f9', fontSize: '13px', fontWeight: '600' }}>{visitor._count.sessions}</td>
                    <td style={{ padding: '12px', color: '#f1f5f9', fontSize: '13px', fontWeight: '600' }}>{visitor._count.pageViews}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ background: visitor.isRegistered ? COLORS.success : '#334155', color: '#fff', padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                        {visitor.isRegistered ? 'Registered' : 'Guest'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '8px' }}>
            <button onClick={() => setVisitorPage(Math.max(0, visitorPage - 1))} disabled={visitorPage === 0} style={{ padding: '8px 16px', background: visitorPage === 0 ? '#1e293b' : COLORS.primary, color: visitorPage === 0 ? '#64748b' : '#0f172a', border: 'none', borderRadius: '8px', cursor: visitorPage === 0 ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600' }}>Previous</button>
            <button onClick={() => setVisitorPage(visitorPage + 1)} disabled={!visitors || (visitorPage + 1) * 10 >= visitors.total} style={{ padding: '8px 16px', background: (!visitors || (visitorPage + 1) * 10 >= visitors.total) ? '#1e293b' : COLORS.primary, color: (!visitors || (visitorPage + 1) * 10 >= visitors.total) ? '#64748b' : '#0f172a', border: 'none', borderRadius: '8px', cursor: (!visitors || (visitorPage + 1) * 10 >= visitors.total) ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600' }}>Next</button>
          </div>
        </ChartCard>

        <div style={{ marginTop: '32px' }}>
          <ChartCard title="Recent Events" subtitle="Latest user interactions">
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {recentEvents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No events tracked yet</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {recentEvents.map((event, idx) => (
                    <div key={idx} style={{ background: '#0f172a', padding: '16px', borderRadius: '8px', border: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>{event.eventName}</div>
                        <div style={{ color: '#64748b', fontSize: '12px' }}>{event.eventCategory} • {event.page}</div>
                      </div>
                      <div style={{ color: '#94a3b8', fontSize: '12px', whiteSpace: 'nowrap' }}>{formatTime(event.timestamp, 'MMM dd, HH:mm')}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ChartCard>
        </div>
      </div>

      {/* Visitor Details Modal */}
      {showVisitorModal && (
        <VisitorDetailsModal
          visitor={selectedVisitor}
          onClose={handleCloseModal}
          loading={loadingVisitorDetails}
          useLocalTime={useLocalTime}
        />
      )}
    </div>
  );
};

export default Dashboard;